import { displayNameFromEmail, normalizedEmail, participantColor, } from "./participantColor.js";
const PRESENCE_TTL_MS = 120_000;
/** Hide block "sta editando" when presence was not refreshed by typing for this long. */
export const EDITING_PRESENCE_IDLE_MS = 30_000;
export function wavePresenceLabel(p) {
    const name = p.displayName?.trim();
    return name && name.length > 0 ? name : p.sub;
}
export function resolvePresenceLabel(presence, participants = []) {
    const direct = presence.displayName?.trim();
    if (direct)
        return direct;
    const match = participants.find((p) => p.sub === presence.sub);
    const participantDisplayName = match?.displayName?.trim();
    if (participantDisplayName)
        return participantDisplayName;
    const participantEmail = match?.email?.trim();
    if (participantEmail)
        return displayNameFromEmail(participantEmail);
    return presence.sub;
}
export function participantEmail(sub, participants = []) {
    const match = findParticipantBySub(sub, participants);
    const email = match?.email?.trim();
    return email && email.length > 0 ? email : null;
}
/** Email key for participant accent color — roster, block owner, remote cursors. */
export function participantAccentEmail(sub, participants = [], hints = {}) {
    const fromParticipants = participantEmail(sub, participants);
    if (fromParticipants)
        return fromParticipants;
    const hinted = hints.email?.trim();
    if (hinted && hinted.length > 0)
        return hinted;
    const session = hints.sessionEmail?.trim();
    if (session && session.includes("@"))
        return session;
    const label = hints.label?.trim() ?? "";
    if (label.includes("@"))
        return label;
    return null;
}
/** Single hash key for avatar color — matches widget owner when identity is the same user. */
export function participantColorKey(sub, participants = [], hints = {}) {
    const accent = participantAccentEmail(sub, participants, hints);
    const normalized = normalizedEmail(accent);
    if (normalized)
        return normalized;
    const matched = findParticipantBySub(sub, participants);
    if (matched?.sub?.trim())
        return matched.sub.trim();
    const trimmed = sub.trim();
    return trimmed.length > 0 ? trimmed : "anonymous";
}
export function participantAccentColor(sub, participants = [], hints = {}) {
    return participantColor(participantColorKey(sub, participants, hints));
}
function emailFromPresenceRecords(sub, presences) {
    for (const presence of presences) {
        if (!isSameParticipantSub(presence.sub, sub))
            continue;
        const name = presence.displayName?.trim();
        if (name && name.includes("@"))
            return name;
    }
    return null;
}
/** True when two Cognito subs refer to the same user (exact or federated tail). */
export function isSameParticipantSub(a, b) {
    const left = a.trim();
    const right = b.trim();
    if (!left || !right)
        return false;
    if (left === right)
        return true;
    const leftTail = left.split("|").pop() ?? left;
    const rightTail = right.split("|").pop() ?? right;
    return leftTail.length > 0 && leftTail === rightTail;
}
/** Same user by Cognito sub tail or by normalized email (roster vs widget / invite sub). */
export function isSameParticipantIdentity(subA, subB, participants = [], hintsA = {}, hintsB = {}) {
    if (isSameParticipantSub(subA, subB))
        return true;
    const emailA = normalizedEmail(participantAccentEmail(subA, participants, hintsA));
    const emailB = normalizedEmail(participantAccentEmail(subB, participants, hintsB));
    if (emailA && emailB && emailA === emailB)
        return true;
    const sessionA = normalizedEmail(hintsA.sessionEmail);
    const sessionB = normalizedEmail(hintsB.sessionEmail);
    if (sessionA && emailB && sessionA === emailB)
        return true;
    if (sessionB && emailA && sessionB === emailA)
        return true;
    return false;
}
function selfAccentHints(currentUserEmail) {
    const session = currentUserEmail?.trim();
    return session && session.includes("@") ? { sessionEmail: session } : {};
}
function isRosterPeerSub(sub, currentSub, participants, currentUserEmail) {
    if (!sub.trim())
        return false;
    return !isSameParticipantIdentity(sub, currentSub, participants, {}, selfAccentHints(currentUserEmail));
}
function findParticipantBySub(sub, participants) {
    const direct = participants.find((p) => p.sub === sub);
    if (direct)
        return direct;
    return participants.find((p) => isSameParticipantSub(p.sub, sub));
}
function findConnectedUserBySub(sub, onlineBySub) {
    const direct = onlineBySub.get(sub);
    if (direct)
        return direct;
    for (const [key, user] of onlineBySub) {
        if (isSameParticipantSub(key, sub))
            return user;
    }
    return undefined;
}
function findLastSeenBySub(sub, lastSeenBySub) {
    const direct = lastSeenBySub.get(sub);
    if (direct)
        return direct;
    for (const [key, row] of lastSeenBySub) {
        if (isSameParticipantSub(key, sub))
            return row;
    }
    return undefined;
}
/** True when persisted authorName is just the opaque Cognito sub (not a human label). */
export function commentAuthorNameLooksLikeSub(authorName, authorSub) {
    const name = authorName?.trim() ?? "";
    const sub = authorSub.trim();
    if (!name)
        return true;
    if (name === sub)
        return true;
    const subTail = sub.split("|").pop() ?? sub;
    if (name === subTail)
        return true;
    if (name.includes("@"))
        return false;
    if (name.length >= 16 && /^[0-9a-f-]+$/i.test(name))
        return true;
    return false;
}
/** Resolve a human-readable author label for a Yjs comment (never raw email). */
export function resolveCommentAuthorLabel(comment, participants = []) {
    const match = findParticipantBySub(comment.authorSub, participants);
    let email = match?.email?.trim();
    const stored = comment.authorName?.trim() ?? "";
    if (!email && stored.includes("@"))
        email = stored;
    const participantDisplayName = match?.displayName?.trim();
    if (participantDisplayName)
        return participantDisplayName;
    if (stored && !stored.includes("@") && !commentAuthorNameLooksLikeSub(stored, comment.authorSub)) {
        return stored;
    }
    if (email)
        return displayNameFromEmail(email);
    return comment.authorSub;
}
/** Block owner identity for timeline gutter (parity iOS `blockAuthorLabel`). */
export function blockAuthorLabel(authorSub, authorName, participants = [], context) {
    const sub = authorSub?.trim() ?? "";
    if (!sub)
        return null;
    const stored = authorName?.trim() ?? "";
    const labelHint = stored.includes("@") ? stored : undefined;
    const sessionEmail = context?.sessionEmail?.trim();
    const ownerSub = context?.ownerSub?.trim() ?? "";
    const sessionSub = context?.sessionSub?.trim() ?? "";
    let email = participantAccentEmail(sub, participants, {
        label: labelHint,
        sessionEmail: sessionEmail && sessionSub && isSameParticipantSub(sub, sessionSub)
            ? sessionEmail
            : undefined,
    });
    if (!email && ownerSub && isSameParticipantSub(sub, ownerSub)) {
        email = participantAccentEmail(ownerSub, participants, {
            label: labelHint,
            sessionEmail: sessionEmail && sessionSub && isSameParticipantSub(ownerSub, sessionSub)
                ? sessionEmail
                : undefined,
        });
    }
    const label = resolveCommentAuthorLabel({ authorSub: sub, authorName: stored || sub }, participants);
    return { sub, label, email };
}
export function isPresenceFresh(p, nowMs = Date.now()) {
    const updated = Date.parse(p.updatedAt);
    if (Number.isNaN(updated))
        return true;
    return nowMs - updated < PRESENCE_TTL_MS;
}
export function isActivelyEditingPresence(presence, nowMs = Date.now()) {
    if (!presence.blockAnchorId)
        return false;
    if (!isPresenceFresh(presence, nowMs))
        return false;
    const updated = Date.parse(presence.updatedAt);
    if (Number.isNaN(updated))
        return true;
    return nowMs - updated < EDITING_PRESENCE_IDLE_MS;
}
export function editorsOnBlock(presences, blockId, currentSub) {
    return presences.filter((p) => !isSameParticipantSub(p.sub, currentSub) &&
        p.blockAnchorId === blockId &&
        isActivelyEditingPresence(p));
}
export function remoteEditorsLabel(editors, participants = []) {
    const names = editors.map((e) => resolvePresenceLabel(e, participants));
    if (names.length === 0)
        return "";
    if (names.length === 1)
        return `${names[0]} sta editando`;
    if (names.length === 2)
        return `${names[0]} e ${names[1]} stanno editando`;
    if (names.length === 3) {
        return `${names[0]}, ${names[1]} e ${names[2]} stanno editando`;
    }
    return `${names[0]}, ${names[1]} e altri stanno editando`;
}
export function activePresences(presences, currentSub) {
    return presences.filter((p) => !isSameParticipantSub(p.sub, currentSub) && isPresenceFresh(p));
}
export function presenceSessionKey(presence) {
    const clientId = presence.clientId?.trim();
    return clientId && clientId.length > 0 ? clientId : "default";
}
export function presenceRecordKey(presence) {
    return `${presence.sub}::${presenceSessionKey(presence)}`;
}
export function mergePresenceRecord(presences, incoming) {
    const key = presenceRecordKey(incoming);
    const filtered = presences.filter((p) => presenceRecordKey(p) !== key);
    if (isPresenceFresh(incoming)) {
        return [...filtered, incoming];
    }
    return filtered;
}
/** Merge HTTP poll results into local state without wiping subscription updates. */
export function mergePolledPresences(existing, polled) {
    const byKey = new Map();
    for (const record of existing) {
        if (isPresenceFresh(record)) {
            byKey.set(presenceRecordKey(record), record);
        }
    }
    for (const record of polled) {
        if (isPresenceFresh(record)) {
            byKey.set(presenceRecordKey(record), record);
        }
    }
    return [...byKey.values()];
}
function selfDeviceCount(presences, currentSub, participants, currentUserEmail) {
    const hints = selfAccentHints(currentUserEmail);
    const clientIds = new Set();
    for (const presence of presences) {
        if (!isSameParticipantIdentity(presence.sub, currentSub, participants, {}, hints)) {
            continue;
        }
        if (!isPresenceFresh(presence))
            continue;
        clientIds.add(presenceSessionKey(presence));
    }
    return clientIds.size;
}
function selfIsOnline(presences, currentSub, participants, currentUserEmail) {
    return selfDeviceCount(presences, currentSub, participants, currentUserEmail) > 0;
}
function sortHeaderPresenceEntries(entries) {
    const selfRows = entries.filter((e) => e.isSelf);
    const others = entries.filter((e) => !e.isSelf);
    others.sort((a, b) => {
        if (a.isOnline !== b.isOnline)
            return a.isOnline ? -1 : 1;
        return a.label.localeCompare(b.label);
    });
    return [...selfRows, ...others];
}
export function connectedUsers(presences, currentSub, participants = []) {
    const others = activePresences(presences, currentSub);
    const bySub = new Map();
    for (const presence of others) {
        const existing = bySub.get(presence.sub) ?? [];
        existing.push(presence);
        bySub.set(presence.sub, existing);
    }
    const result = [];
    for (const [sub, records] of bySub) {
        const clientIds = new Set();
        for (const record of records) {
            clientIds.add(presenceSessionKey(record));
        }
        let latest = records[0];
        for (const record of records) {
            if (Date.parse(record.updatedAt) > Date.parse(latest.updatedAt)) {
                latest = record;
            }
        }
        result.push({
            sub,
            label: resolvePresenceLabel(latest, participants),
            deviceCount: clientIds.size,
            latest,
        });
    }
    return result.sort((a, b) => a.label.localeCompare(b.label));
}
export function participantPresenceEntries(participants, presences, lastSeen, currentSub, ownerSub, options = {}) {
    const includeSelf = options.includeSelf ?? false;
    const sessionEmail = options.currentUserEmail ?? null;
    const rosterSubs = new Set();
    const addPeer = (sub) => {
        if (isRosterPeerSub(sub, currentSub, participants, sessionEmail)) {
            rosterSubs.add(sub);
        }
    };
    for (const participant of participants) {
        addPeer(participant.sub);
    }
    const owner = ownerSub?.trim();
    if (owner)
        addPeer(owner);
    for (const presence of presences) {
        if (!isPresenceFresh(presence))
            continue;
        addPeer(presence.sub);
    }
    for (const row of lastSeen) {
        addPeer(row.sub);
    }
    if (includeSelf && currentSub.trim()) {
        rosterSubs.add(currentSub);
    }
    const onlineBySub = new Map();
    for (const user of connectedUsers(presences, currentSub, participants)) {
        onlineBySub.set(user.sub, user);
    }
    const lastSeenBySub = new Map();
    for (const row of lastSeen) {
        lastSeenBySub.set(row.sub, row);
    }
    const result = [];
    for (const sub of rosterSubs) {
        const participant = findParticipantBySub(sub, participants);
        const online = findConnectedUserBySub(sub, onlineBySub);
        const seen = findLastSeenBySub(sub, lastSeenBySub);
        let email = participant?.email?.trim() || participantEmail(sub, participants);
        if (!email && online?.label.includes("@")) {
            email = online.label.trim();
        }
        if (!email) {
            email = emailFromPresenceRecords(sub, presences);
        }
        if (!email && seen?.displayName?.includes("@")) {
            email = seen.displayName.trim();
        }
        let label = participant?.displayName?.trim() ||
            (email ? displayNameFromEmail(email) : "");
        if (!label && online) {
            label = online.label;
        }
        if (!label && seen?.displayName?.trim()) {
            label = seen.displayName.trim();
        }
        if (!label)
            label = sub;
        let lastSeenAt = seen?.lastSeenAt ?? null;
        if (online) {
            lastSeenAt = online.latest.updatedAt;
        }
        const selfHints = selfAccentHints(sessionEmail);
        const isSelf = includeSelf &&
            isSameParticipantIdentity(sub, currentSub, participants, {}, selfHints);
        let resolvedOnline = Boolean(online);
        let resolvedDeviceCount = online?.deviceCount ?? 0;
        if (isSelf) {
            if (!email) {
                email =
                    participantAccentEmail(currentSub, participants, selfHints) ?? email;
            }
            resolvedOnline = selfIsOnline(presences, currentSub, participants, sessionEmail);
            resolvedDeviceCount = selfDeviceCount(presences, currentSub, participants, sessionEmail);
        }
        result.push({
            sub,
            label: isSelf ? "Tu" : label,
            email,
            isOnline: resolvedOnline,
            deviceCount: resolvedDeviceCount,
            lastSeenAt,
            isSelf,
        });
    }
    const selfEmail = normalizedEmail(participantAccentEmail(currentSub, participants, selfAccentHints(sessionEmail)));
    const deduped = includeSelf && selfEmail
        ? result.filter((entry) => {
            if (entry.isSelf)
                return true;
            const peerEmail = normalizedEmail(entry.email);
            return !(peerEmail && peerEmail === selfEmail);
        })
        : result;
    return sortHeaderPresenceEntries(deduped);
}
const CURSOR_STALE_MS = 5_000;
export function isCursorStale(presence, nowMs = Date.now()) {
    const updated = Date.parse(presence.updatedAt);
    if (Number.isNaN(updated))
        return false;
    return nowMs - updated > CURSOR_STALE_MS;
}
export function buildWavePresenceDiagnostics(input) {
    const entries = participantPresenceEntries(input.participants, input.presences, input.lastSeen, input.currentSub, input.ownerSub, { includeSelf: true });
    return {
        participantsCount: input.participants.length,
        presencesCount: input.presences.length,
        lastSeenCount: input.lastSeen.length,
        rosterCount: entries.length,
        onlineCount: entries.filter((e) => e.isOnline).length,
        currentSub: input.currentSub,
        realtimeConnected: input.realtimeConnected,
        presencePollingOnly: input.presencePollingOnly,
        lastError: input.lastError ?? null,
    };
}
export function formatWavePresenceDiagnostics(d) {
    return [
        `participants=${d.participantsCount}`,
        `presences=${d.presencesCount}`,
        `lastSeen=${d.lastSeenCount}`,
        `roster=${d.rosterCount}`,
        `online=${d.onlineCount}`,
        `sub=${d.currentSub}`,
        `ws=${d.realtimeConnected}`,
        `pollOnly=${d.presencePollingOnly}`,
        `error=${d.lastError ?? "—"}`,
    ].join("\n");
}
export function remoteCursorsOnBlock(presences, blockId, currentSub) {
    return editorsOnBlock(presences, blockId, currentSub).filter((p) => p.cursorOffset != null && p.cursorOffset >= 0);
}
